import {
  Component,
  OnInit,
  Output,
  Input,
  EventEmitter,
  ElementRef,
  inject,
  ChangeDetectorRef,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { AlertifyService } from "../_models/alertify.service";
import { AppMaterialModule } from "../app-material/app-material.module";

import {
  JurisdictionResult,
  SearchDefinition,
} from "../regcompare/statediff-model";
import { HelpInfoService } from "../_common/help-info.service";
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { PublicationService } from "../publication/publication.service";
import { AppSettings } from "../_constants/AppSettings";
import {
  getCanadaProvinceName,
  getDisplayCountryName,
  getMexicoStateName,
} from "../_constants/CountryForAcronym";
import { TOPIC_LIST } from "../_constants/TopicList";
import { MatButtonModule } from "@angular/material/button";
@Component({
  selector: "app-search-filter",
  templateUrl: "./search-filter.component.html",
  imports: [
    FormsModule,
    CommonModule,
    AppMaterialModule,
    MatExpansionModule,
    MatDialogModule,
    MatButtonModule,
  ],
  styleUrls: ["./search-filter.component.scss"],
  standalone: true,
})
export class SearchFilterComponent implements OnInit {
  isCAAccess = true;
  isLoading = false;
  states: { code: string; name: string }[] = [];
  filterJurisdictions: JurisdictionResult[] = [];
  filterTopics: string = "";
  searchWord = "";
  selectedDefinitionId = 0;
  displayDef: SearchDefinition[] = [];
  originalSearchDef: SearchDefinition[] = [];
  isSearchDefPanelOpen = false;

  // Question Type checkboxes
  rulebookChecked: boolean = true;
  applicabilityChecked: boolean = true;

  @Output() applySearchEvent = new EventEmitter<{
    st: JurisdictionResult[];
    tk: string;
    keywords: string;
    questionType?: number;
  }>();
  @Output() deleteSearchDefinitionEvent = new EventEmitter<{ cKey: string }>();

  constructor(
    public dialog: MatDialog,
    public dataService: PublicationService,
    private alertifyService: AlertifyService,
    private cdr: ChangeDetectorRef,
    private helpInfoService: HelpInfoService,
  ) {}

  ngOnInit(): void {
    this.searchWord = "";
  }

  ClearAll() {
    this.clearJurisdiction();
    this.ClearTopic();
    this.searchWord = "";
    this.selectedDefinitionId = 0; // No Search Definition is selected.

    // Reset Question Type checkboxes to default (both checked)
    this.rulebookChecked = true;
    this.applicabilityChecked = true;

    this.applySearchEvent.emit({
      st: this.filterJurisdictions,
      tk: this.filterTopics,
      keywords: this.searchWord,
      questionType: undefined,
    });
  }

  clearJurisdiction() {
    // Clear the jurisdictions
    this.filterJurisdictions = [];

    // Trigger change detection to update UI immediately
    this.cdr.detectChanges();
  }

  ClearTopic() {
    this.filterTopics = "";
  }

  clearSelection() {
    // Clear the selected definition ID
    this.selectedDefinitionId = 0;

    // Clear search terms
    this.searchWord = "";

    // Clear filter jurisdictions and topics
    this.clearJurisdiction();
    this.ClearTopic();

    // Reset Question Type checkboxes to both checked
    this.rulebookChecked = true;
    this.applicabilityChecked = true;

    // Emit the clear event to update the search
    this.applySearchEvent.emit({
      st: [],
      tk: "",
      keywords: "",
      questionType: undefined,
    });
  }

  // Get topic names from comma-separated keys for display
  getTopicNamesFromKeys(): string[] {
    if (!this.filterTopics) return [];
    const keys = this.filterTopics.split(",").filter((key) => key.trim());
    return keys.map((key) => {
      const topic = TOPIC_LIST.find((t) => t.key.toString() === key.trim());
      return topic ? topic.name : key.trim();
    });
  }

  /**
   * Open the dialog to display help information.
   * @param $event: click mouse event
   * @param index: the index of the help info. See ./_constants/AppSettting for more detail.
   */
  openHelpInfo($evt: MouseEvent, index: number): void {
    this.helpInfoService.open($evt, index, {
      position: { left: "50px", top: "100px" },
      panelClass: "help-dialog-panel",
    });
  }

  async showTopicPopup() {
    const result = await this.openTopicTreeDialog(this.filterTopics);

    if (result !== null) {
      this.filterTopics = result;
      return;
    }
  }

  async showJurPopup() {
    const sessionKey = "authorized_jurisdictions";

    // Check if data exists in session storage
    const cachedData = sessionStorage.getItem(sessionKey);
    if (cachedData) {
      try {
        const jurisdictions: JurisdictionResult[] = JSON.parse(cachedData);
        if (jurisdictions && jurisdictions.length > 0) {
          const result = await this.openJurisdictionListDialog(
            this.filterJurisdictions,
            jurisdictions,
          );

          if (result !== null) {
            this.filterJurisdictions = result;
          }
          return;
        }
      } catch (error) {
        console.warn("Failed to parse cached jurisdiction data:", error);
        // Remove invalid cached data
        sessionStorage.removeItem(sessionKey);
      }
    }

    // If no valid cached data, fetch from backend
    this.isLoading = true;
    this.dataService.getAuthorizedJurisdiction().subscribe(
      async (res) => {
        this.isLoading = false;
        let jurisdictions: JurisdictionResult[] = res;
        if (jurisdictions !== undefined && jurisdictions.length > 0) {
          // Store in session storage for future use
          try {
            sessionStorage.setItem(sessionKey, JSON.stringify(jurisdictions));
          } catch (error) {
            console.warn("Failed to cache jurisdiction data:", error);
          }

          const result = await this.openJurisdictionListDialog(
            this.filterJurisdictions,
            jurisdictions,
          );

          if (result !== null) {
            this.filterJurisdictions = result;
            return;
          }
        }
      },
      (err) => {
        console.error("Failed to get authorized jurisdiction: ", err);
        this.isLoading = false;
      },
    );
  }

  async openTopicTreeDialog(filter: string = "") {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { selected: filter || "", topics: TOPIC_LIST };
    dialogConfig.width = "50vw";
    dialogConfig.maxHeight = "85vh";
    dialogConfig.panelClass = "topic-dialog";
    const dialogRef = this.dialog.open(TopicListDialog, dialogConfig);

    return dialogRef
      .afterClosed()
      .toPromise()
      .then((result) => {
        return Promise.resolve(result);
      });
  }

  async openJurisdictionListDialog(
    filter: JurisdictionResult[],
    jurisdictions: JurisdictionResult[],
  ): Promise<JurisdictionResult[] | null> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      selected: filter || [],
      jurisdictions: jurisdictions,
    };
    dialogConfig.width = "1020px";
    dialogConfig.maxHeight = "90vh";
    dialogConfig.panelClass = "jurisdiction-dialog";
    const dialogRef = this.dialog.open(JurisdictionListDialog, dialogConfig);

    return dialogRef
      .afterClosed()
      .toPromise()
      .then((result) => {
        return Promise.resolve(result);
      });
  }

  openSearchDef(selectName?: string): void {
    this.isSearchDefPanelOpen = true;
    this.isLoading = true;
    this.dataService.getSavedSearchDefinitions().subscribe(
      (res) => {
        this.isLoading = false;
        console.log("Saved search definitions:", res);
        // Store both original and display versions
        this.originalSearchDef = res || [];
        this.displayDef = res || [];
        if (selectName) {
          const match = this.displayDef.find(
            (d) => d.searchName === selectName,
          );
          if (match) {
            this.selectedDefinitionId = match.id;
            this.cdr.detectChanges();
          }
        }
      },
      (err) => {
        this.isLoading = false;
        this.originalSearchDef = [];
        this.displayDef = [];
        if (err?.status === 404) {
          this.alertifyService.warning("No saved search definitions found.");
        } else {
          console.error("Failed to get saved search definitions:", err);
          this.alertifyService.error(
            "Failed to load saved search definitions.",
          );
        }
      },
    );
  }

  clearAllSavedSearch() {}

  //Click on the radio button of saved search definition.
  onDefinitionSelected(cId: number) {
    console.log("radio selected: " + cId);
    this.selectedDefinitionId = cId;

    // Find the selected search definition
    const selectedDef = this.displayDef.find((def) => def.id === cId);
    if (selectedDef) {
      // Populate search filters from the saved search definition
      this.searchWord = selectedDef.keyword || "";

      // Set jurisdictions
      if (
        selectedDef.jurisdictionFilter &&
        selectedDef.jurisdictionFilter.length > 0
      ) {
        try {
          // Parse JSON string to array
          const jurisdictions =
            typeof selectedDef.jurisdictionFilter === "string"
              ? JSON.parse(selectedDef.jurisdictionFilter)
              : selectedDef.jurisdictionFilter;
          this.filterJurisdictions = Array.isArray(jurisdictions)
            ? jurisdictions
            : [];
        } catch (error) {
          console.error("Failed to parse jurisdictions JSON:", error);
          this.filterJurisdictions = [];
        }
      } else {
        this.filterJurisdictions = [];
      }

      // Set topics
      if (selectedDef.topicFilter && selectedDef.topicFilter.length > 0) {
        this.filterTopics = selectedDef.topicFilter;
      } else {
        this.filterTopics = "";
      }

      // Set question type checkboxes
      if (
        selectedDef.questionType !== undefined &&
        selectedDef.questionType !== 0
      ) {
        if (selectedDef.questionType === 1) {
          // Only rulebook
          this.rulebookChecked = true;
          this.applicabilityChecked = false;
        } else if (selectedDef.questionType === 2) {
          // Only applicability
          this.rulebookChecked = false;
          this.applicabilityChecked = true;
        }
      } else {
        // Both question types (default when questionType is undefined)
        this.rulebookChecked = true;
        this.applicabilityChecked = true;
      }

      // Trigger change detection
      this.cdr.detectChanges();

      console.log("Loaded search definition:", {
        keyword: this.searchWord,
        jurisdictions: this.filterJurisdictions.length,
        topics: this.filterTopics,
        rulebook: this.rulebookChecked,
        applicability: this.applicabilityChecked,
      });
    }
  }

  isDefinitionSelected(current: number) {
    return current == this.selectedDefinitionId;
  }

  onSearchDefinition(searchDef: string) {
    if (!searchDef || searchDef.trim() === "") {
      // If search is empty, show all original definitions
      this.displayDef = [...this.originalSearchDef];
      return;
    }

    // Filter the original definitions based on search text
    const searchText = searchDef.toLowerCase().trim();
    this.displayDef = this.originalSearchDef.filter(
      (def) =>
        def.searchName.toLowerCase().includes(searchText) ||
        def.keyword.toLowerCase().includes(searchText),
    );

    // Show alert if no matches found
    if (this.displayDef.length === 0) {
      this.alertifyService.warning("Not found");
    }
  }

  onDeleteSearchDefinition(searchId: number, searchName: string) {
    // Show confirmation dialog
    this.alertifyService.confirmOkCancel(
      "STP AuditHub",
      `Are you sure you want to delete the saved search "${searchName}"?`,
      () => {
        this.isLoading = true;
        this.dataService.deleteSearchDefinition(searchId).subscribe(
          (res) => {
            this.isLoading = false;
            // Show success message
            this.alertifyService.success(
              `Search definition "${searchName}" has been deleted successfully.`,
            );

            // Remove the deleted item from the display list
            this.displayDef = this.displayDef.filter(
              (def) => def.id !== searchId,
            );

            // Clear selection if the deleted item was selected
            if (this.selectedDefinitionId === searchId) {
              this.selectedDefinitionId = 0;

              // Clear all search terms and filters
              this.searchWord = "";
              this.filterJurisdictions = [];
              this.filterTopics = "";
              // Reset question type checkboxes to default
              this.rulebookChecked = true;
              this.applicabilityChecked = true;
            }

            // Trigger change detection
            this.cdr.detectChanges();
          },
          (err) => {
            this.isLoading = false;
            console.error("Failed to delete search definition:", err);
            this.alertifyService.error(
              "Failed to delete the search definition. Please try again.",
            );
          },
        );
      },
      () => {
        // Cancel callback - do nothing
      },
    );
  }

  onAdvancedSearch() {
    // Navigate to Advanced Search Page
    this.alertifyService.error("Sorry, in construction...");
    return;
  }

  onSearch() {
    if (this.hasInvalidSearchChars) {
      this.alertifyService.warning(
        "Search term contains unsupported characters: * ? ! ^ ~ : \\",
      );
      return;
    }
    if (this.hasUnbalancedQuotes) {
      this.alertifyService.warning(
        "Search term contains an unclosed quote. Please wrap your phrase in matching quote marks.",
      );
      return;
    }
    if (this.hasQuotesNotAtBoundaries) {
      this.alertifyService.warning(
        'Quotes must wrap the entire search term, e.g. "exact phrase".',
      );
      return;
    }
    if (this.hasShortQuotedTerm) {
      this.alertifyService.warning(
        "Quoted phrases must contain at least 2 characters.",
      );
      return;
    }
    // Validate that at least one question type is selected
    if (!this.rulebookChecked && !this.applicabilityChecked) {
      this.alertifyService.error("At least one question type is required.");
      return;
    }

    // Calculate questionType based on checkbox states
    let questionType: number | undefined = undefined;
    if (this.rulebookChecked && !this.applicabilityChecked) {
      questionType = 1; // Only rulebook
    } else if (!this.rulebookChecked && this.applicabilityChecked) {
      questionType = 2; // Only applicability
    }
    // If both are checked, questionType remains undefined (don't send filter)

    this.applySearchEvent.emit({
      st: this.filterJurisdictions,
      tk: this.filterTopics,
      keywords: this.searchWord,
      questionType: questionType,
    });
  }

  // Handle question type checkbox changes
  onQuestionTypeChange(type: string, checked: boolean) {
    if (type === "rulebook") {
      this.rulebookChecked = checked;
    } else if (type === "applicability") {
      this.applicabilityChecked = checked;
    }

    // If both are unchecked, show warning
    if (!this.rulebookChecked && !this.applicabilityChecked) {
      this.alertifyService.error("At least one question type is required.");
    }
  }

  // Check if search should be disabled
  isSearchDisabled(): boolean {
    return !this.rulebookChecked && !this.applicabilityChecked;
  }

  get hasInvalidSearchChars(): boolean {
    return /[*?!^~:\\]/.test(this.searchWord);
  }

  get hasUnbalancedQuotes(): boolean {
    const dq = (this.searchWord.match(/"/g) || []).length;
    const sq = (this.searchWord.match(/'/g) || []).length;
    return dq % 2 !== 0 || sq % 2 !== 0;
  }

  get hasQuotesNotAtBoundaries(): boolean {
    const w = this.searchWord.trim();
    if (!w.includes('"') && !w.includes("'")) return false;
    return !(
      (w.startsWith('"') && w.endsWith('"')) ||
      (w.startsWith("'") && w.endsWith("'"))
    );
  }

  get hasShortQuotedTerm(): boolean {
    const pattern = /"([^"]*)"|'([^']*)'/g;
    let m: RegExpExecArray | null;
    while ((m = pattern.exec(this.searchWord)) !== null) {
      const content = (m[1] ?? m[2]).trim();
      if (content.length < 2) return true;
    }
    return false;
  }
}

/**
 * Dialog window to display the Jurisdictions.
 */
@Component({
  selector: "jurisdiction-list",
  templateUrl: "jurisdiction-list.html",
  styleUrls: ["search-filter.component.scss"],
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    FormsModule,
    AppMaterialModule,
    MatExpansionModule,
  ],
})
export class JurisdictionListDialog implements OnInit {
  tips: string = "";
  unlockFlag?: boolean;
  build: string = "";
  collection: string = "";
  allCollections: string[] = ["USA", "CAN", "MEX", "Global"];
  theJurisdictions: string[][] = [[], [], [], []];
  data = inject(MAT_DIALOG_DATA);
  usaJurisdictions: string[] = [];
  canJurisdictions: string[] = [];
  mexJurisdictions: string[] = [];
  globalJurisdictions: JurisdictionResult[] = [];
  filterJurisdiction: JurisdictionResult[] = [];
  tempFilterJurisdiction: JurisdictionResult[] = []; // Temporary array for dialog changes
  constructor(public dialogRef: MatDialogRef<JurisdictionListDialog>) {}

  ngOnInit() {
    // Create a deep copy of the selected jurisdictions to work with temporarily
    this.tempFilterJurisdiction = JSON.parse(
      JSON.stringify(this.data.selected),
    );
    this.filterJurisdiction = this.data.selected;
    this.allCollections.forEach((c) => {
      let x = this.data.jurisdictions
        .filter((j: JurisdictionResult) => j.country === c)
        .map((j: { jurisdiction: any }) => j.jurisdiction);
      this.theJurisdictions[this.allCollections.indexOf(c)] =
        x[0]?.split(",") ?? [];
      if (
        this.collection.length === 0 &&
        this.theJurisdictions[this.allCollections.indexOf(c)].length > 0
      ) {
        this.collection = c;
      }
      if (this.theJurisdictions[0]?.indexOf("50") > -1) {
        // If "FED" doesn't exist, add "FED" to the top of the list.
        if (this.theJurisdictions[0].indexOf("FED") === -1) {
          this.theJurisdictions[0].unshift("FED");
        }

        // Remove "50" first, then add all the USA states to the list if "50" exists.
        const index = this.theJurisdictions[0].indexOf("50");
        if (index > -1) {
          this.theJurisdictions[0].splice(index, 1);
          this.theJurisdictions[0].push(...this.getUs50States());
          this.theJurisdictions[0] = [...new Set(this.theJurisdictions[0])];
        }
      }
      this.theJurisdictions[this.allCollections.indexOf(c)].sort();
    });
    // For international jurisdictions, we use the country name as the jurisdiction name, and display it at the end of the list. If ther is jursidiction with  the country, then display the province level too.
    this.globalJurisdictions = this.data.jurisdictions.filter(
      (j: JurisdictionResult) =>
        j.country !== "USA" && j.country !== "CAN" && j.country !== "MEX",
    );

    this.theJurisdictions[3] = this.globalJurisdictions
      .map((j: JurisdictionResult) => j.country)
      .sort();
    if (this.collection.length === 0 && this.globalJurisdictions.length > 0) {
      this.collection = "Global";
    }

    // If there are selected jurisdictions, activate the first tab that has selections.
    if (this.tempFilterJurisdiction.length > 0) {
      const hasUSA = this.tempFilterJurisdiction.some(
        (j) => j.country === "USA",
      );
      const hasCAN = this.tempFilterJurisdiction.some(
        (j) => j.country === "CAN",
      );
      const hasMEX = this.tempFilterJurisdiction.some(
        (j) => j.country === "MEX",
      );
      const hasGlobal = this.tempFilterJurisdiction.some(
        (j) =>
          j.country !== "USA" && j.country !== "CAN" && j.country !== "MEX",
      );
      if (hasUSA) this.collection = "USA";
      else if (hasCAN) this.collection = "CAN";
      else if (hasMEX) this.collection = "MEX";
      else if (hasGlobal) this.collection = "Global";
    }
  }

  // Method to check if there are any changes made to the jurisdiction selection
  hasChanges(): boolean {
    return (
      JSON.stringify(this.tempFilterJurisdiction) !==
      JSON.stringify(this.data.selected)
    );
  }

  // Get the USA states list.
  getUs50States(): string[] {
    return AppSettings.US_STATES.map((s) => s);
  }

  // Select all jurisdiction
  selectAllJurisdictions(collection: string) {
    if (collection === "Global") {
      // Add all the international jurisdictions with jurisdiction set to the
      // country code so the backend can identify them as international protocol filters.
      const globalJurisdictions = this.data.jurisdictions
        .filter(
          (j: JurisdictionResult) =>
            j.country !== "USA" && j.country !== "CAN" && j.country !== "MEX",
        )
        .map((j: JurisdictionResult) => ({ country: j.country, jurisdiction: j.country }));
      this.tempFilterJurisdiction.push(...globalJurisdictions);
    } else {
      // Add the jurisdictions of this collection.
      const jurisdictions = this.theJurisdictions[
        this.allCollections.indexOf(collection)
      ].map((jurisdiction) => ({
        country: collection,
        jurisdiction,
      }));
      // Remove the duplicated jurisdictions if there are any.
      this.tempFilterJurisdiction = this.tempFilterJurisdiction.filter(
        (j) => j.country !== collection,
      );
      this.tempFilterJurisdiction.push(...jurisdictions);
    }
  }

  // Clear all jurisdiction for the collection
  clearAllJurisdictions(collection: string) {
    if (collection === "Global") {
      // Remove all the international jurisdictions.
      this.tempFilterJurisdiction = this.tempFilterJurisdiction.filter(
        (j) =>
          j.country === "USA" || j.country === "CAN" || j.country === "MEX",
      );
    } else {
      // Remove the jurisdictions of this collection.
      this.tempFilterJurisdiction = this.tempFilterJurisdiction.filter(
        (j) => j.country !== collection,
      );
    }
  }

  isJurisdictionSelected(jurisdiction: string, collection: string): boolean {
    if (collection === "Global") {
      // For global collection, we only filter by country name, so the jurisdiction is the same as the country name.
      return this.tempFilterJurisdiction.some(
        (j) => j.country === jurisdiction,
      );
    }
    return this.tempFilterJurisdiction.some(
      (j) => j.jurisdiction.includes(jurisdiction) && j.country === collection,
    );
  }

  // When the user clicks on the checkbox of a jurisdiction, add or remove this jurisdiction to/from the temporary filter list.
  jurisdictionChanged($event: any, item: string, collection: string) {
    if ($event) {
      // Add this jurisdiction to the temporary filter list.
      if (collection === "Global") {
        // For global collection, we only filter by country name, so the jurisdiction is the same as the country name.
        this.tempFilterJurisdiction.push({ country: item, jurisdiction: item });
      } else {
        this.tempFilterJurisdiction.push({
          country: collection,
          jurisdiction: item,
        });
      }
    } else {
      if (collection === "Global") {
        // For global collection, we only filter by country name, so the jurisdiction is the same as the country name.
        this.tempFilterJurisdiction = this.tempFilterJurisdiction.filter(
          (j) => !(j.country === item),
        );
      } else {
        this.tempFilterJurisdiction = this.tempFilterJurisdiction.filter(
          (j) => !(j.country === collection && j.jurisdiction === item),
        );
      }
    }
  }

  getDisplayItem(item: string, collection: string): string {
    if (collection === "Global") {
      return getDisplayCountryName(item);
    } else if (collection === "CAN") {
      return getCanadaProvinceName(item);
    } else if (collection === "MEX") {
      return getMexicoStateName(item);
    } else {
      return item;
    }
  }

  getSortedJurisdictions(collection: string): string[] {
    const jurisdictions =
      this.theJurisdictions[this.allCollections.indexOf(collection)];
    const sorted = [...jurisdictions].sort((a, b) => {
      const displayA = this.getDisplayItem(a, collection);
      const displayB = this.getDisplayItem(b, collection);
      return displayA.localeCompare(displayB);
    });
    const fedIndex = sorted.indexOf("FED");
    if (fedIndex > 0) {
      sorted.splice(fedIndex, 1);
      sorted.unshift("FED");
    }
    return sorted;
  }

  onCategoryChange(category: string) {
    this.collection = category;
  }

  hasSelectionInCollection(collection: string): boolean {
    if (collection === "Global") {
      return this.tempFilterJurisdiction.some(
        (j) =>
          j.country !== "USA" && j.country !== "CAN" && j.country !== "MEX",
      );
    }
    return this.tempFilterJurisdiction.some((j) => j.country === collection);
  }

  onApply(): void {
    // Apply the temporary changes to the actual filter
    this.dialogRef.close(this.tempFilterJurisdiction);
  }

  onCancel(): void {
    // Discard temporary changes and close dialog without applying
    this.dialogRef.close(null);
  }
}

/**
 * Dialog window to display and select Topics.
 */
@Component({
  selector: "topic-list",
  templateUrl: "topic-list.html",
  styleUrls: ["search-filter.component.scss"],
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    FormsModule,
    AppMaterialModule,
    CommonModule,
  ],
})
export class TopicListDialog implements OnInit {
  data = inject(MAT_DIALOG_DATA);
  selectedTopics: string[] = [];
  tempSelectedTopics: string[] = []; // Temporary array for dialog changes
  expandedGroups: Set<string> = new Set(); // Track expanded groups, default collapsed
  activeGroup: string = "environment"; // Default to first tab

  constructor(public dialogRef: MatDialogRef<TopicListDialog>) {}

  ngOnInit() {
    // Create a copy of the selected topics to work with temporarily
    // Convert comma-separated string to array
    const selectedArray = this.data.selected
      ? this.data.selected.split(",").filter((item) => item.trim())
      : [];
    this.tempSelectedTopics = [...selectedArray];
    this.selectedTopics = selectedArray;
    // Expand groups that have at least one selected topic
    const groups = ["environment", "hazardous_materials", "health_safety", "industry_categories"];
    for (const group of groups) {
      if (this.getTopicsByGroup(group).some((t: any) => selectedArray.includes(t.key.toString()))) {
        this.expandedGroups.add(group);
      }
    }
  }

  // TrackBy function for performance optimization
  trackByFn(index: number, item: any) {
    return item.key;
  }

  // Method to check if there are any changes made to the topic selection
  hasChanges(): boolean {
    const original = this.data.selected
      ? this.data.selected.split(",").filter((item: string) => item.trim()).sort()
      : [];
    const current = [...this.tempSelectedTopics].sort();
    return JSON.stringify(current) !== JSON.stringify(original);
  }

  // Get all topics
  getFilteredTopics() {
    return this.data.topics;
  }

  // Check if a topic is selected
  isTopicSelected(topicKey: string): boolean {
    return this.tempSelectedTopics.includes(topicKey);
  }

  // Handle topic selection change
  topicChanged(event: any, topicKey: string) {
    if (event.checked) {
      // Add topic to selection
      if (!this.tempSelectedTopics.includes(topicKey)) {
        this.tempSelectedTopics.push(topicKey);
      }
    } else {
      // Remove topic from selection
      this.tempSelectedTopics = this.tempSelectedTopics.filter(
        (topic) => topic !== topicKey,
      );
    }
  }

  // Select all topics
  selectAllTopics() {
    const allTopics = this.data.topics;
    allTopics.forEach((topic) => {
      const topicKeyStr = topic.key.toString();
      if (!this.tempSelectedTopics.includes(topicKeyStr)) {
        this.tempSelectedTopics.push(topicKeyStr);
      }
    });
  }

  // Clear all selected topics
  clearAllTopics() {
    this.tempSelectedTopics = [];
  }

  // Get count of selected topics
  getSelectedCount(): number {
    return this.tempSelectedTopics.length;
  }

  // Handle tab change
  onGroupChange(groupKey: string) {
    this.activeGroup = groupKey;
  }

  // Select all topics in the active group
  selectAllTopicsInGroup(groupKey: string) {
    if (!groupKey) return;

    const groupTopics = this.getTopicsByGroup(groupKey);
    groupTopics.forEach((topic) => {
      if (topic.disabled) return;
      const topicKeyStr = topic.key.toString();
      if (!this.tempSelectedTopics.includes(topicKeyStr)) {
        this.tempSelectedTopics.push(topicKeyStr);
      }
    });
  }

  // Clear all topics in the active group
  clearAllTopicsInGroup(groupKey: string) {
    if (!groupKey) return;

    const groupTopics = this.getTopicsByGroup(groupKey);
    groupTopics.forEach((topic) => {
      if (topic.disabled) return;
      const topicKeyStr = topic.key.toString();
      this.tempSelectedTopics = this.tempSelectedTopics.filter(
        (selectedTopic) => selectedTopic !== topicKeyStr,
      );
    });
  }

  // Get topics by group based on key ranges
  getTopicsByGroup(groupKey: string) {
    const allTopics = this.data.topics;
    switch (groupKey) {
      case "environment":
        return allTopics.filter((t) => t.key >= 1 && t.key <= 99);
      case "hazardous_materials":
        return allTopics.filter((t) => t.key >= 100 && t.key <= 199);
      case "health_safety":
        return allTopics.filter((t) => t.key >= 200 && t.key <= 299);
      case "industry_categories":
        return allTopics.filter((t) => t.key >= 300);
      default:
        return [];
    }
  }

  // Check if some (but not all) topics in a group are selected
  isGroupPartiallySelected(groupKey: string): boolean {
    const groupTopics = this.getTopicsByGroup(groupKey);
    const selectedCount = groupTopics.filter((topic) =>
      this.tempSelectedTopics.includes(topic.name),
    ).length;
    return selectedCount > 0 && selectedCount < groupTopics.length;
  }

  // Toggle all topics in a group
  toggleGroup(groupKey: string, checked: boolean) {
    const groupTopics = this.getTopicsByGroup(groupKey);

    if (checked) {
      // Add all group topics to selection
      groupTopics.forEach((topic) => {
        if (!this.tempSelectedTopics.includes(topic.name)) {
          this.tempSelectedTopics.push(topic.name);
        }
      });
    } else {
      // Remove all group topics from selection
      groupTopics.forEach((topic) => {
        this.tempSelectedTopics = this.tempSelectedTopics.filter(
          (selectedTopic) => selectedTopic !== topic.name,
        );
      });
    }
  }

  // Check if a group is expanded
  isGroupExpanded(groupKey: string): boolean {
    return this.expandedGroups.has(groupKey);
  }

  // Toggle group expansion
  toggleGroupExpansion(groupKey: string) {
    if (this.expandedGroups.has(groupKey)) {
      this.expandedGroups.delete(groupKey);
    } else {
      this.expandedGroups.add(groupKey);
    }
  }

  // Check if all topics in a group are selected
  isGroupSelected(groupKey: string): boolean {
    const groupTopics = this.getTopicsByGroup(groupKey);
    if (groupTopics.length === 0) return false;

    return groupTopics
      .filter((topic) => !topic.disabled)
      .every((topic) => this.tempSelectedTopics.includes(topic.key.toString()));
  }

  // Toggle selection of all topics in a group
  toggleGroupSelection(groupKey: string, checked: boolean) {
    if (checked) {
      this.selectAllTopicsInGroup(groupKey);
    } else {
      this.clearAllTopicsInGroup(groupKey);
    }
  }

  onApply(): void {
    // Apply the temporary changes to the actual selection
    // Convert array back to comma-separated string
    const selectedString = this.tempSelectedTopics.join(",");
    this.dialogRef.close(selectedString);
  }

  onCancel(): void {
    // Discard temporary changes and close dialog without applying
    this.dialogRef.close(null);
  }
}
